import type { TagDisplayCase } from '@platewell/core';
import { useCollections, useConnectionStatus, useImportCollectionId, useStore, useTagDisplayCase } from '@platewell/query';
import { Link } from '@tanstack/react-router';
import { useEffect, useState } from 'react';
import { BUILD_VERSION } from '../../lib/build-info.js';
import { isSentryConfigured, openFeedbackForm } from '../../lib/sentry.js';
import { loadBuildDetail, versionReport, type BuildDetail } from './build-info.js';

export function GeneralTab() {
  const collections = useCollections();
  const tagDisplayCase = useTagDisplayCase();
  const importCollectionId = useImportCollectionId();
  const { readOnly } = useConnectionStatus();
  const store = useStore();

  return (
    <>
      <section className="section-group">
        <h2 className="section-title">Imports</h2>
        <p className="hint">Choose which collection imported recipes are added to.</p>
        <div className="row">
          <select
            className="select"
            style={{ maxWidth: 260 }}
            value={importCollectionId}
            disabled={readOnly}
            onChange={(event) => {
              void store.setImportCollectionId(event.target.value);
            }}
          >
            {collections.map((collection) => (
              <option key={collection.id} value={collection.id}>
                {collection.name}
              </option>
            ))}
          </select>
        </div>
      </section>

      <section className="section-group">
        <h2 className="section-title">Tags</h2>
        <p className="hint">Choose how tags appear across this household. Stored tags are unchanged.</p>
        <div className="row">
          <select
            className="select"
            style={{ maxWidth: 260 }}
            value={tagDisplayCase}
            disabled={readOnly}
            onChange={(event) => {
              void store.setTagDisplayCase(event.target.value as TagDisplayCase);
            }}
          >
            <option value="preserve">As imported</option>
            <option value="lower">Lowercase</option>
            <option value="title">Title case</option>
          </select>
        </div>
      </section>

      <section className="section-group">
        <h2 className="section-title">Trash</h2>
        <p className="hint">
          Deleted recipes stay in trash so a delete on one device never surprises anyone on another.
        </p>
        <div className="row">
          <Link className="btn" to="/trash">
            Open trash
          </Link>
        </div>
      </section>

      <section className="section-group">
        <h2 className="section-title">How syncing works here</h2>
        <p className="hint">
          Every device keeps a full copy of this household&rsquo;s recipes, so the app works with no
          signal at all. When two people edit the same recipe at once, the edits are merged rather
          than one overwriting the other — different fields both survive, and edits to the same
          paragraph are woven together character by character.
        </p>
      </section>

      {isSentryConfigured() && (
        <section className="section-group">
          <h2 className="section-title">Report a problem</h2>
          <p className="hint">
            Tell us what went wrong. Your report reaches us with the app version and recent errors
            attached, never your recipes.
          </p>
          <div className="row">
            <button type="button" className="btn" onClick={() => void openFeedbackForm()}>
              Report a problem
            </button>
          </div>
        </section>
      )}

      <About />
    </>
  );
}

/**
 * The version line, on this tab alone.
 *
 * It used to repeat under every section of the old scroll. One place is enough, and this
 * is the tab somebody already opens when asked "which version are you on?".
 */
function About() {
  const [detail, setDetail] = useState<BuildDetail | null>(null);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    let cancelled = false;
    void loadBuildDetail().then((value) => {
      if (!cancelled) setDetail(value);
    });
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (!copied) return;
    const timer = setTimeout(() => setCopied(false), 2000);
    return () => clearTimeout(timer);
  }, [copied]);

  async function copy() {
    try {
      await navigator.clipboard.writeText(versionReport(detail));
      setCopied(true);
    } catch {
      // A browser that refuses the clipboard leaves the text on screen to select by hand.
    }
  }

  return (
    <footer className="settings-about">
      <span>Platewell {BUILD_VERSION}</span>
      <span className="spacer" />
      <button type="button" className="btn small" onClick={() => void copy()}>
        {copied ? 'Copied' : 'Copy info'}
      </button>
    </footer>
  );
}
